/** Product-bound browser event wrapper. The transport and idempotency policy
 * live in @engine/attribution; this file only supplies Wisprs identity cookies. */
import {
  emitBrowserEvent,
  getOrCreateClientId,
  getOrCreateSessionId,
} from '@engine/attribution';

export type ClientEventPath =
  | '/api/events/track'
  | '/api/events/conversion'
  | '/api/events/gtm'
  | '/api/events/rum';

export type ClientEventBody = { name: string } & Record<string, unknown>;

type PostHogBridge = { identity?: () => { sessionId?: string; distinctId?: string } | undefined };

function postHogBridge(): PostHogBridge | undefined {
  if (typeof window === 'undefined') return undefined;

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  return (window as unknown as { __wisprsPostHog?: PostHogBridge }).__wisprsPostHog;
}

/**
 * Resolve once PostHog has loaded (or after `maxMs`), so the first page view of a
 * visit can carry the PostHog session id. No-op when PostHog isn't configured.
 */
export async function waitForPostHog(maxMs = 1500): Promise<void> {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY || typeof window === 'undefined') return;

  const started = Date.now();

  while (!postHogBridge() && Date.now() - started < maxMs) {
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
}

export function emitClientEvent(path: ClientEventPath, body: ClientEventBody): void {
  let posthog: { sessionId?: string; distinctId?: string } | undefined;

  try {
    posthog = postHogBridge()?.identity?.();
  } catch {
    posthog = undefined;
  }

  emitBrowserEvent(path, posthog?.sessionId || posthog?.distinctId ? { ...body, posthog } : body, {
    getIdentity: () => ({
      clientId: getOrCreateClientId({ cookieName: 'wisprs_cid', maxAgeSeconds: 60 * 60 * 24 * 365 * 2 }) || undefined,
      sessionId: getOrCreateSessionId({
        storageKey: 'wisprs_sid',
        timestampKey: 'wisprs_sid_ts',
        cookieName: 'wisprs_sid',
      }) || undefined,
    }),
  });
}
